import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { formatUserDisplayName } from "@presentation/hooks/useUserDisplayNames";
import type { TeamMembership, UserSummary } from "@domain/schemas/workspace";

type ManagerOption = { id: string; name: string };
const NO_NEW_MANAGER: ManagerOption = { id: "", name: "No change" };

interface AcceptProjectMoveDialogProps {
  teamId: string;
  requestId: string;
  /**
  The target team's own members - the only people eligible to become the project's new Manager
  (the backend rejects anyone else). Already available on `TeamDetailPage` with no extra fetch.
  */
  teamMemberships: readonly TeamMembership[];
  userNames: Readonly<Record<string, UserSummary | undefined>>;
  onClose: () => void;
  onSuccess: () => void;
}

/**
Accepting a move keeps the project's existing Managers even if none of them belong to the target
team - violating the "project Manager must be a team member" invariant. Lets the accepting Manager
promote one of the target team's own members to Manager in the same step; the backend rejects the
accept outright if none of the current Managers qualify and no replacement is given here.
*/
export const AcceptProjectMoveDialog: React.FC<AcceptProjectMoveDialogProps> = ({ teamId, requestId, teamMemberships, userNames, onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const options: ManagerOption[] = [NO_NEW_MANAGER, ...teamMemberships.map((member) => ({ id: member.userId, name: formatUserDisplayName(userNames[member.userId], member.userId) }))];
  const [selected, setSelected] = useState<ManagerOption>(NO_NEW_MANAGER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await teamManagementService.acceptProjectMoveAsync(teamId, requestId, selected.id || undefined);
      onSuccess();
      onClose();
    } catch (error) {
      setErrorMessage(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not accept this move request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Accept project move" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0 }}>
          If none of the project&apos;s current Managers are members of this team, pick one of this team&apos;s own members to promote to Manager. Otherwise this is optional.
        </p>
        <div>
          <label htmlFor="accept-move-new-manager" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
            New Manager
          </label>
          <DropDownList
            id="accept-move-new-manager"
            style={{ width: "100%" }}
            data={options}
            textField="name"
            dataItemKey="id"
            value={selected}
            onChange={(event) => setSelected(event.value as ManagerOption)}
          />
        </div>
        {errorMessage && <div style={{ color: "#7f1d1d" }}>{errorMessage}</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={isSubmitting} onClick={handleSubmit}>
          Accept
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
